/**
 * <Emoji native="❤️"/> — рендерит эмодзи в Apple-стиле через `<img>` с CDN.
 *
 * Использует emoji-datasource-apple через jsdelivr — отдаёт отдельные PNG
 * по unicode-codepoint'ам. Это В РАЗЫ дешевле эмодзи-марта `<em-emoji>`
 * (custom element с shadow DOM и mutation-observer'ами на каждый смайлик):
 *   - В DOM просто <img>, не custom element
 *   - Нет JS runtime per emoji
 *   - Браузер кэширует PNG'ки → повторные рендеры мгновенные
 *   - loading="lazy" → off-screen смайлики не грузятся пока не нужны
 *
 * splitTextWithEmojis(text) — режет текст на массив строк и `<Emoji>`
 * по unicode emoji codepoints (через emoji-regex для ZWJ-последовательностей).
 *
 * Если CDN не отдал спрайт (редкий или новый эмодзи) — onError fallback
 * на native unicode.
 */
import { useState, type ReactNode } from "react";
import emojiRegex from "emoji-regex";


// Self-hosted twemoji SVG (тот же source что для pill реакций).
// Раньше тут был jsdelivr CDN → каждый emoji отдельный HTTP request +
// native-флэш перед заменой на CDN-картинку.
const TWEMOJI_BASE = "/twemoji/svg";


/** twemoji.convert.toCodePoint logic: hex codes joined "-", skip VS16 (fe0f) если нет ZWJ. */
export function emojiToTwemojiCode(native: string): string {
  const codes: string[] = [];
  for (const c of native) {
    codes.push(c.codePointAt(0)!.toString(16));
  }
  const hasZwj = codes.includes("200d");
  if (!hasZwj) return codes.filter(c => c !== "fe0f").join("-");
  return codes.join("-");
}


const _prefetchedSvg = new Set<string>();
/** Прогрев twemoji-SVG для эмодзи из текста композера (16.09.2026): пока юзер
 *  печатает, картинка уже едет в кэш — большой одиночный эмодзи после отправки
 *  не показывает пустой квадрат до загрузки файла. Не больше 12 за вызов. */
export function prefetchEmojiSvgs(text: string): void {
  if (!text || typeof Image === "undefined") return;
  let n = 0;
  for (const m of text.matchAll(emojiRegex())) {
    const cp = emojiToTwemojiCode(m[0]);
    if (_prefetchedSvg.has(cp)) continue;
    _prefetchedSvg.add(cp);
    const img = new Image();
    img.src = `${TWEMOJI_BASE}/${cp}.svg`;
    if (++n >= 12) break;
  }
}

export function Emoji({ native, eager }: { native: string; size?: string | number; eager?: boolean }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <span className="emoji-fallback">{native}</span>;
  }
  const cp = emojiToTwemojiCode(native);
  // alt={native}: КРИТИЧНО для copy/paste. При выделении и копировании
  // браузер кладёт alt-текст в text/plain — если alt="" то emoji теряется
  // (юзер скопировал «привет 😀» — вставилось «привет »).
  return (
    <img
      className="emoji-img"
      src={`${TWEMOJI_BASE}/${cp}.svg`}
      alt={native}
      width={22}
      height={22}
      decoding={eager ? "sync" : "async"}
      draggable={false}
      onError={() => setFailed(true)}
    />
  );
}


export function splitTextWithEmojis(text: string, _size?: string | number): ReactNode[] {
  void _size;
  if (!text) return [];
  const re = emojiRegex();
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(re)) {
    const start = m.index ?? 0;
    if (start > last) out.push(text.slice(last, start));
    out.push(<Emoji key={`e:${i++}`} native={m[0]}/>);
    last = start + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
